export default class MatomoTrackEventCc {
    /**
     * page name of the tracked event -
     * Syntax: pageName
     * @type {string | undefined}
     */
    #category;
    /**
     * which event was triggered by which element -
     * Syntax: event:area.element.?modifier
     * @type {string | undefined}
     */
    #action;
    /**
     * specification of the event e.g. values -
     * Syntax: element:name1=value1|name2=value2|...
     * @type {string | undefined}
     */
    #name;
    /**
     * saved to use during name creation
     * @type {string | undefined}
     */
    #element;

    /**
     *  @param {string} category - defined page name in camelCase (pageName)
     *  @returns {MatomoTrackEventCc}
     */
    setCategory(category) {
        this.#category = category;
        return this;
    }

    /**
     * returns matomo Event Category
     * @returns {string|undefined}
     */
    getCategory() {
        return this.#category;
    }

    /**
     *  pass all information to create the actions string with following syntax: event:area.element.modifier
     *  @param {object} action - object which contains all elements we need to create the tracking action
     *  @param {string} action.event - event we want to track
     *  @param {string} action.area - general area where event is located on the page
     *  @param {string} action.element - element which is involved
     *  @param {string} [action.modifier] - specification of event/element
     *  @returns {MatomoTrackEventCc}
     */
    setAction(action) {
        const {
            event, area, element, modifier
        } = action;
        this.#element = element;
        this.#action = `${event}:${area}.${element}${modifier ? `.${modifier}` : ''}`;
        return this;
    }

    /**
     * returns matomo Event Action
     * @returns {string|undefined}
     */
    getAction() {
        return this.#action;
    }

    /**
     *  pass all values/ information we want to track, to create the name string with following syntax: element:name1=value1|name2=value2|...
     *  @param {Object.<string, string>} values - object with "name: value" pairs we want to track
     *  @returns {MatomoTrackEventCc}
     */
    setName(values) {
        let name = `${this.#element}:`;
        for (const [key, value] of Object.entries(values)) {
            name += `${key}=${this.parseToCamelCase(value)}|`;
        }
        this.#name = name.slice(0, -1);
        return this;
    }

    /**
     * returns matomo Event Name
     * @returns {string|undefined}
     */
    getName() {
        return this.#name;
    }

    /**
     * pushes the event to matomo
     *  @returns {void}
     */
    pushEvent() {
        const isPaqAvailable = typeof window._paq !== 'undefined' && window._paq !== null;
        const dataAvailable = this.#category && this.#action && this.#name;
        if (isPaqAvailable && dataAvailable) {
            const event = ['trackEvent', this.#category, this.#action, this.#name];
            try {
                window._paq.push(event);
            } catch (e) {
                window.Sentry.captureException(e);
            }
        } else {
            const missingValues = [
                !isPaqAvailable ? 'matomo implementation ' : '',
                !this.#category ? 'category ' : '',
                !this.#action ? 'action ' : '',
                !this.#name ? 'name' : ''
            ];

            const msg = `push matomo event failed due to missing: ${missingValues[0]}${missingValues[1]}${missingValues[2]}${missingValues[3]}`;
            window.Sentry.captureMessage(msg);
        }
    }

    /**
     * transform a string into a camelCase string
     @param {string} value
     @return {string}
     */
    parseToCamelCase(value) {
        const str = String(value);
        return str.toLowerCase().replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, (match, index) => {
            if (+match === 0) return '';
            return index === 0 ? match.toLowerCase() : match.toUpperCase();
        });
    }
}
